import { useState } from "react";
import { api } from "../lib/config";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, DollarSign, ArrowRight, Repeat, Calendar } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin dialog for updating a customer's price.
 * Two scopes:
 *   - "single"    → updates ONE booking only (requires `booking`)
 *   - "recurring" → updates the customer's recurring service price + cascades
 *                   to every future unpaid booking (requires `customer`)
 *
 * Props:
 *   open, onOpenChange
 *   booking   — optional: the booking being priced (for single-visit mode)
 *   customer  — optional: the user object (for recurring mode)
 *   allowScopeToggle: bool  — when both booking + customer.recurring_schedule
 *                            exist, show a radio group so admin can pick
 *                            "this visit only" vs "all future recurring visits"
 *   onSaved(payload)
 */
export default function UpdatePriceDialog({
  open, onOpenChange, booking, customer, allowScopeToggle = true, onSaved,
}) {
  const oldSinglePrice = Number(booking?.price ?? 0);
  const oldRecurringPrice = Number(customer?.recurring_schedule?.base_price ?? 0);
  const hasRecurring = !!(customer?.recurring_schedule && customer.recurring_schedule.frequency && customer.recurring_schedule.frequency !== "one_time" && customer.recurring_schedule.status === "active");
  const canToggle = allowScopeToggle && !!booking && hasRecurring;

  const [scope, setScope] = useState(booking ? "single" : "recurring");
  const [newPrice, setNewPrice] = useState(
    booking ? String(oldSinglePrice || "") : String(oldRecurringPrice || "")
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const oldValue = scope === "single" ? oldSinglePrice : oldRecurringPrice;
  const parsed = Number(newPrice);
  const delta = Number.isFinite(parsed) ? parsed - oldValue : 0;

  const submit = async () => {
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Enter a valid new price"); return;
    }
    setBusy(true);
    try {
      let result;
      if (scope === "single") {
        if (!booking?.booking_id) throw new Error("Missing booking");
        ({ data: result } = await api.post(`/admin/bookings/${booking.booking_id}/price`, {
          new_price: parsed, note: note.trim() || undefined,
        }));
        toast.success(`Booking updated to $${parsed.toFixed(2)}`);
      } else {
        const uid = customer?.user_id || booking?.user_id;
        if (!uid) throw new Error("Missing customer");
        ({ data: result } = await api.post(`/admin/customers/${uid}/recurring-price`, {
          new_price: parsed, include_scope: "future",
          note: note.trim() || undefined,
        }));
        toast.success(
          `Recurring price set to $${parsed.toFixed(2)} · ${result.affected_future_bookings} future visits updated`
        );
      }
      onSaved?.(result);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not update price");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="update-price-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] tracking-tight flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-700" /> Update Price
          </DialogTitle>
          <DialogDescription>
            {scope === "single" && booking
              ? `Booking #${booking.booking_id?.slice(-6)} · ${booking.name || "customer"}`
              : `Recurring service for ${customer?.name || "customer"}`}
          </DialogDescription>
        </DialogHeader>

        {canToggle && (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-1 grid grid-cols-2 gap-1"
               data-testid="price-scope-toggle">
            <button type="button" onClick={() => setScope("single")}
                    data-testid="scope-single-btn"
                    className={`px-3 py-2 rounded-xl text-[12px] font-extrabold transition-colors ${scope === "single" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"}`}>
              <Calendar className="inline w-3.5 h-3.5 mr-1" /> This visit
            </button>
            <button type="button" onClick={() => setScope("recurring")}
                    data-testid="scope-recurring-btn"
                    className={`px-3 py-2 rounded-xl text-[12px] font-extrabold transition-colors ${scope === "recurring" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"}`}>
              <Repeat className="inline w-3.5 h-3.5 mr-1" /> All future recurring
            </button>
          </div>
        )}

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Old price</div>
              <div className="text-2xl font-extrabold text-gray-900 mt-0.5" data-testid="old-price">
                ${oldValue.toFixed(2)}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-emerald-700" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">New price</div>
              <div className="text-2xl font-extrabold text-emerald-900 mt-0.5" data-testid="new-price-preview">
                ${Number.isFinite(parsed) ? parsed.toFixed(2) : "—"}
              </div>
            </div>
          </div>
          {Number.isFinite(parsed) && Math.abs(delta) > 0.001 && (
            <div className={`mt-2 text-[11px] font-bold ${delta > 0 ? "text-red-700" : "text-emerald-700"}`}
                 data-testid="price-delta">
              {delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(2)} vs. current
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="np" className="text-[11px] font-bold uppercase tracking-wider text-gray-600">New price</Label>
          <Input id="np" type="number" step="0.01" min="0" value={newPrice}
                 onChange={(e) => setNewPrice(e.target.value)}
                 placeholder="0.00" data-testid="new-price-input" />
        </div>
        <div>
          <Label htmlFor="np-note" className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Note (optional)</Label>
          <Input id="np-note" value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder="e.g. Reduced for loyalty · extra tree removed"
                 data-testid="price-note-input" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="update-price-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="update-price-submit-btn"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white">
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>)
                  : (scope === "single" ? "Save Price" : "Save Recurring Price")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
